import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PenSquare, MoreVertical, Trash2, Loader2, Search, Package } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import { useChatStore } from '../askAnything/chatStore'
import { useRecentProductsStore } from '../askAnything/recentProductsStore'
import MiniWaves from '@/components/animations/MiniWaves'
import { SidebarProductSelector } from './SidebarProductSelector'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

// Add styles for the fade reveal animation and hide scrollbar
const customStyles = `
  @keyframes fadeReveal {
    from {
      opacity: 0.5;
      transform: translateY(-2px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .fade-reveal {
    animation: fadeReveal 0.5s ease-out forwards;
  }
  
  /* Hide scrollbar but maintain functionality */
  .hide-scrollbar {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
  
  .hide-scrollbar::-webkit-scrollbar {
    display: none;  /* Chrome, Safari and Opera */
  }
`

type ThreadsMenuProps = {
  currentThreadId: string
  onNewChatClick: () => void
  switchThread: (threadId: string) => void
}

const ThreadsMenu: React.FC<ThreadsMenuProps> = ({ currentThreadId, onNewChatClick, switchThread }) => {
  const { isDarkMode } = useTheme()
  const customFetch = useCustomFetch()
  const { toast } = useToast()

  const { threads, refreshThreads } = useChatStore()
  const { recentProducts } = useRecentProductsStore()
  const [updatedThreads, setUpdatedThreads] = useState<Set<string>>(new Set())
  const [prevThreads, setPrevThreads] = useState(threads)
  const [searchQuery, setSearchQuery] = useState('')

  // Track thread name changes
  useEffect(() => {
    const newUpdatedThreads = new Set(updatedThreads)
    threads.forEach((thread, i) => {
      const prevThread = prevThreads[i]
      if (prevThread && prevThread.name !== thread.name) {
        newUpdatedThreads.add(thread.id)
        // Remove the thread ID after animation completes
        setTimeout(() => {
          setUpdatedThreads(prev => {
            const next = new Set(prev)
            next.delete(thread.id)
            return next
          })
        }, 500)
      }
    })
    if (newUpdatedThreads.size !== updatedThreads.size) {
      setUpdatedThreads(newUpdatedThreads)
    }
    setPrevThreads(threads)
  }, [threads])

  const [isRenaming, setIsRenaming] = useState(false)
  const [threadBeingRenamed, setThreadBeingRenamed] = useState<string | null>(null)
  const [newThreadName, setNewThreadName] = useState('')
  const [isRenamingLoading, setIsRenamingLoading] = useState(false)
  const [threadToDelete, setThreadToDelete] = useState<string | null>(null)

  const deleteThread = async (threadIdToDelete: string) => {
    if (threads.length <= 1) {
      toast({
        title: 'Cannot delete thread',
        description: 'You need at least one conversation.',
        variant: 'destructive',
      })
      return
    }

    try {
      await customFetch(`/chat/threads/${threadIdToDelete}`, 'DELETE')

      if (threadIdToDelete === currentThreadId) {
        const remainingThreads = threads.filter((thread) => thread.id !== threadIdToDelete)
        switchThread(remainingThreads[0].id)
      }

      await refreshThreads()

      toast({
        title: 'Conversation deleted',
        description: 'The conversation has been removed.',
      })
    } catch (error) {
      toast({
        title: 'Error deleting thread',
        description: 'Failed to delete conversation. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleThreadClick = (threadIdToSwitch: string) => {
    if (threadIdToSwitch !== currentThreadId && !isRenaming) {
      switchThread(threadIdToSwitch)
    }
  }

  const startRenameThread = (threadIdToRename: string) => {
    if (isRenamingLoading) return

    const thread = threads.find((t) => t.id === threadIdToRename)
    if (thread) {
      setNewThreadName(thread.name)
      setThreadBeingRenamed(threadIdToRename)
      setIsRenaming(true)
    }
  }

  async function updateThreadName(threadId: string, name: string) {
    setIsRenamingLoading(true)
    try {
      await customFetch(`/chat/threads/updateChatThreadName`, 'PUT', { chatThreadId: threadId, name })
      await refreshThreads()
    } finally {
      setIsRenamingLoading(false)
      setIsRenaming(false)
      setThreadBeingRenamed(null)
      setNewThreadName('')
    }
  }

  const confirmRenameThread = async (threadId: string) => {
    if (!isRenaming || !newThreadName.trim()) {
      setIsRenaming(false)
      setThreadBeingRenamed(null)
      return
    }

    try {
      await updateThreadName(threadId, newThreadName)

      toast({
        title: 'Conversation renamed',
        description: 'The conversation name has been updated.',
      })
    } catch (error) {
      toast({
        title: 'Error renaming thread',
        description: 'Failed to rename conversation. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const cancelRenameThread = () => {
    setIsRenaming(false)
    setThreadBeingRenamed(null)
    setNewThreadName('')
  }

  const handleDeleteClick = (threadId: string) => {
    setThreadToDelete(threadId)
  }

  const handleDeleteConfirm = async () => {
    if (!threadToDelete) return
    await deleteThread(threadToDelete)
    setThreadToDelete(null)
  }

  const handleDeleteCancel = () => {
    setThreadToDelete(null)
  }

  return (
    <>
      <style>{customStyles}</style>
      <div
        className={`w-[320px] h-full border-r ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} flex flex-col`}
      >
        <div className="pt-8 pb-6 px-5">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <MiniWaves />
              </div>
              <h1 className="text-xl font-bold">Ask Anything</h1>
            </div>
            <Button
              onClick={onNewChatClick}
              variant="ghost"
              size="icon"
              className={isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-900'}
              title="New Chat"
            >
              <PenSquare size={18} />
            </Button>
          </div>

          {/* Search Filter */}
          <div className="mb-8 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
            </div>
            <Input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-10 py-2 ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-gray-50 border-gray-200'}`}
            />
          </div>

          {/* Products Multi-Select */}
          <div className="mb-8">
            <SidebarProductSelector />
          </div>

          {/* Chats Section */}
          <div className="flex items-center justify-between mb-0">
            <h2
              className={`text-sm uppercase tracking-wider font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
            >
              Chats
            </h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto hide-scrollbar">
          <div className="px-4 pt-2 pb-8 space-y-3">
            {threads
              .filter(thread => 
                searchQuery === '' || 
                thread.name.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((thread) => (
              <ContextMenu key={thread.id}>
                <ContextMenuTrigger asChild>
                  <div
                    onClick={() => handleThreadClick(thread.id)}
                    className={`
                      py-1.5 px-2 rounded cursor-pointer transition-colors group
                      ${
                        thread.id === currentThreadId
                          ? isDarkMode
                            ? 'bg-gray-800 text-white'
                            : 'bg-gray-100 text-gray-900'
                          : isDarkMode
                            ? 'hover:bg-gray-800 text-gray-300'
                            : 'hover:bg-gray-50 text-gray-700'
                      }
                    `}
                  >
                    {isRenaming && threadBeingRenamed === thread.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={newThreadName}
                          onChange={(e) => setNewThreadName(e.target.value)}
                          autoFocus
                          disabled={isRenamingLoading}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              confirmRenameThread(thread.id)
                            } else if (e.key === 'Escape') {
                              cancelRenameThread()
                            }
                          }}
                          onBlur={() => confirmRenameThread(thread.id)}
                          className={`h-8 py-1 ${isDarkMode ? 'bg-gray-700 border-gray-600' : ''}`}
                          placeholder="Thread name"
                        />
                        {isRenamingLoading && <Loader2 size={16} className="animate-spin text-gray-400" />}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className={`truncate flex-1 ${updatedThreads.has(thread.id) ? 'fade-reveal' : ''}`}>
                          {thread.name}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical size={14} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className={isDarkMode ? 'bg-gray-800 border-gray-700' : ''}>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation()
                                startRenameThread(thread.id)
                              }}
                              className={`${
                                isDarkMode
                                  ? 'hover:bg-gray-700 focus:bg-gray-700 text-gray-200'
                                  : 'hover:bg-gray-100 focus:bg-gray-100 text-gray-700'
                              } 
                                cursor-pointer transition-colors`}
                            >
                              <PenSquare size={14} className="mr-2" />
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteClick(thread.id)
                              }}
                              className="group w-full cursor-pointer transition-colors"
                            >
                              <div
                                className={`flex items-center w-full ${
                                  isDarkMode
                                    ? 'text-warningRed group-hover:bg-red-900/20'
                                    : 'text-warningRed group-hover:bg-red-50'
                                } rounded px-2 py-1.5`}
                              >
                                <Trash2 size={14} className="mr-2" />
                                Delete
                              </div>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </div>
                </ContextMenuTrigger>
                <ContextMenuContent className={`w-48 ${isDarkMode ? 'bg-gray-800 border-gray-700' : ''}`}>
                  <ContextMenuItem
                    onClick={() => startRenameThread(thread.id)}
                    className={isDarkMode ? 'hover:bg-gray-700 focus:bg-gray-700' : ''}
                  >
                    <PenSquare size={14} className="mr-2" />
                    Rename
                  </ContextMenuItem>
                  <ContextMenuItem
                    onClick={() => handleDeleteClick(thread.id)}
                    className={`text-red-600 focus:text-red-600 ${isDarkMode ? 'hover:bg-gray-700 focus:bg-gray-700' : ''}`}
                  >
                    <Trash2 size={14} className="mr-2" />
                    Delete
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            ))}
          </div>
        </div>
      </div>

      <AlertDialog open={!!threadToDelete} onOpenChange={(open) => !open && handleDeleteCancel()}>
        <AlertDialogContent
          className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-2 !border-warningRed rounded-2xl p-8 outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0`}
        >
          <AlertDialogHeader className="space-y-4">
            <AlertDialogTitle
              className={`text-[26px] font-semibold font-['Manrope'] leading-[29.90px] ${isDarkMode ? 'text-white' : 'text-zinc'}`}
            >
              Delete Conversation
            </AlertDialogTitle>
            <AlertDialogDescription
              className={`text-base font-normal font-['Manrope'] leading-[23px] ${isDarkMode ? 'text-gray-300' : 'text-zinc-700'}`}
            >
              Are you sure you want to delete this conversation? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex items-center gap-3 mt-8 !justify-start">
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="w-[215px] h-11 px-[19px] py-2.5 bg-warningRed rounded shadow-inner justify-center items-center gap-2 inline-flex text-white text-base font-semibold font-['Manrope'] leading-tight focus-visible:outline-none focus-visible:ring-0"
            >
              <Trash2 size={20} />
              Delete Forever
            </AlertDialogAction>
            <AlertDialogCancel
              className={`w-[86px] h-10 px-4 py-2.5 rounded shadow-inner border !border-warningRed justify-center items-center gap-3 inline-flex focus-visible:outline-none focus-visible:ring-0 ${isDarkMode ? 'bg-transparent' : ''}`}
            >
              <span className="text-warningRed text-base font-semibold font-['Manrope'] leading-tight">Cancel</span>
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default ThreadsMenu
